/**
 * Provider-neutral tool definitions for the Luminaria assistant.
 *
 * Single source of truth for the ~28 tools exposed by both the in-app chat
 * assistant (luminaria-api's Anthropic tool format) and the MCP server
 * (JSON-Schema `inputSchema` format). These used to be two independently
 * hand-maintained ~600-900 line files that had already drifted (the api
 * copy had `load_skill`, absent from mcp; both copies independently omitted
 * `iso_exercise` from an income-type enum). Each side now derives its own
 * wire format from this list instead of retyping it.
 *
 * STANDARD_TOOLS: exist in both the in-app assistant and the MCP server.
 * API_ONLY_TOOLS: exist only in the in-app assistant (e.g. load_skill,
 *   which loads reference material meant for the assistant's own context,
 *   not something an external MCP client would call).
 * MCP_ONLY_TOOLS: exist only in the MCP server — debug/introspection tools
 *   (provider health, quote cache stats) and MCP-specific compositions
 *   (execute_batch) that the in-app assistant doesn't need.
 */

import { portfolioTools } from './portfolio.js';
import { taxTools } from './tax.js';
import { incomeTools } from './income.js';
import { transactionsTools } from './transactions.js';
import { targetProfilesTools } from './targetProfiles.js';
import { syncImportTools } from './syncImport.js';
import { marketDebugTools } from './marketDebug.js';
import { assistantTools } from './assistant.js';
import { costBasisTools } from './costBasis.js';
import { messageTools } from './messages.js';
import { assertDefined } from '../../utils/assertDefined.js';
import type { ToolDefinition } from './types.js';

export type { ToolDefinition } from './types.js';

const TOOLS: Record<string, ToolDefinition> = {
  ...portfolioTools,
  ...taxTools,
  ...incomeTools,
  ...transactionsTools,
  ...targetProfilesTools,
  ...syncImportTools,
  ...marketDebugTools,
  ...assistantTools,
  ...costBasisTools,
  ...messageTools,
};

/** The named tools in order; a misspelled name is a load-time error, never a silent gap. */
const pick = (names: readonly string[]): ToolDefinition[] => names.map((name) => {
  const tool = TOOLS[name];
  assertDefined(tool, `Unknown tool in list: ${name}`);
  return tool;
});

export const STANDARD_TOOLS: ToolDefinition[] = pick([
  'get_portfolios',
  'get_portfolio_holdings',
  'calculate_tax_estimate',
  'get_realized_gains',
  'get_accounts',
  'get_cash_balances',
  'get_income_summary',
  'get_portfolio_performance',
  'get_benchmark_comparison',
  'get_open_options',
  'get_recent_transactions',
  'get_quarterly_estimates',
  'get_allocation',
  'get_quote',
  'get_dividend_history',
  'get_external_income_sources',
  'get_w2_income',
  'get_1099_income',
  'get_daily_change',
  'get_transactions_diagnostic',
  'snaptrade_fetch_activities',
  'create_transaction',
  'update_transaction',
  'delete_transaction',
  'list_target_profiles',
  'get_target_profile',
  'create_target_profile',
  'update_target_profile',
  'delete_target_profile',
  'apply_target_profile',
  'get_tax_loss_harvest_candidates',
  'get_cost_basis_report',
  'get_symbol_lot_assignments',
  'get_available_lots',
  'get_corporate_actions_needing_review',
  'reassign_sale_lots',
  'recalculate_lots',
  'update_holding_cost_basis',
  'add_manual_lot',
  'remove_manual_lot',
  'resolve_corporate_action',
  'get_sync_status',
  'sync_accounts',
  'update_sync_settings',
  'get_messages',
  'get_message',
  'resolve_message',
  'update_messages',
]);

export const API_ONLY_TOOLS: ToolDefinition[] = pick([
  'load_skill',
  'import_transactions',
]);

export const MCP_ONLY_TOOLS: ToolDefinition[] = pick([
  'get_quotes_batch',
  'get_provider_status',
  'get_quote_cache_stats',
  'clear_quote_cache',
  'get_option_quote_debug',
  'diagnose_symbol',
  'probe_providers_health',
  'reset_provider_state',
  'get_recent_quote_failures',
  'clear_recent_quote_failures',
  'get_watchlist',
  'execute_batch',
  'get_income_projections',
]);

/** All tool definitions, standard + api-only + mcp-only. */
export const ALL_TOOLS: ToolDefinition[] = [
  ...STANDARD_TOOLS,
  ...API_ONLY_TOOLS,
  ...MCP_ONLY_TOOLS,
];
